import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getInfoArteFinal,
  getInfoArteFinalEdit,
  getPdfLanguageByArteFinal,
  updateArteFinal,
  updateArteFinalWithCategories,
} from '../api/arteFinalApi';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { createArteFinal, createArteFinalImprimible } from '../helpers/ArteFinal/arteFinalHelper';
import { useFormik } from 'formik';
import { REPEATABLE_CODES } from '../utils/constants';

export const useUpdateArteFinal = (idArte, shouldClose = true) => {
  const { handleClose } = useCloseEditArteFinal();
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (values) => {
      const token = await getAccessTokenSilently();

      const tagsProcesados =
        values.tagsInput
          ?.split(',')
          .map((t) => t.trim())
          .filter((t) => t.length > 0) || [];

      const valuesConTags = {
        ...values,
        tags: tagsProcesados,
      };

      await updateArteFinalWithCategories(idArte, valuesConTags, token);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['info-arte-final', idArte],
      });
      queryClient.invalidateQueries({
        queryKey: ['categories-tree-by-arte', idArte],
      });
      queryClient.invalidateQueries({
        queryKey: ['formats-arte-final', idArte],
      });
      queryClient.invalidateQueries('all-artes-finales-master');
      toast.success('Arte final actualizado con éxito');
      if (shouldClose) handleClose();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  return { mutation };
};

export const useGetPdfLanguages = (idArte) => {
  return useQuery({
    queryKey: ['pdf-languages', idArte],
    queryFn: () => getPdfLanguageByArteFinal(idArte),
  });
};

export const useCloseEditArteFinal = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClose = () => {
    let newPath = location.pathname;

    if (location.pathname.endsWith('/')) {
      newPath = newPath.slice(0, -1);
    }

    newPath = newPath.substring(0, newPath.lastIndexOf('/'));

    navigate(newPath || '/', { replace: true });
  };

  return { handleClose };
};

export const useGetEditArteFinal = (id) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['info-arte-final-edit', id],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getInfoArteFinalEdit(id, token);
    },
  });
};

export const useGetInfoArteFinal = (id) => {
  const { getAccessTokenSilently } = useAuth0();

  const { data, isLoading } = useQuery(['info-arte-final', id], async () => {
    const token = await getAccessTokenSilently();
    return getInfoArteFinal(id, token);
  });

  return { data, isLoading };
};

export const useValidateArteFinal = (idArte) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (values) => {
      let token = await getAccessTokenSilently();
      updateArteFinal(idArte, values, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['info-arte-final', idArte],
      });
      queryClient.invalidateQueries('all-artes-finales-master');
      toast.success('¡Actualización de la validación exitosa!');
    },
    onError: () => {
      toast.error('Error al actualizar la validación');
    },
  });

  return { mutation };
};

export const useCreateArteFinal = (activeStep, setActiveStep) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const handleClose = () => {
    let newPath = location.pathname;

    if (location.pathname.endsWith('/')) {
      newPath = newPath.slice(0, -1);
    }

    newPath = newPath.substring(0, newPath.lastIndexOf('/'));

    navigate(newPath || '/', { replace: true });
  };

  const mutationFirstStep = useMutation(createArteFinalImprimible, {
    onSuccess: () => {
      queryClient.invalidateQueries('all-artes-finales-master');
      handleClose();
    },
    onError: () => {
      toast.error('Error al crear el arte final imprimible');
    },
  });

  const mutationLastStep = useMutation({
    mutationFn: createArteFinal,
    onSuccess: () => {
      queryClient.invalidateQueries('all-artes-finales-master');
      handleClose();
    },
    onError: (err) => {
      if (err?.response?.status === 511) {
        toast.error('Error: código duplicado');
        return;
      }

      toast.error('Error al crear el arte final');
    },
  });

  const initialValuesArteFinal = {
    name: '',
    category: [],
    centerType: [],
    favourite: [],
    active: true,
    isMontaje: false,
    isDescargable: false,
    isInformational: false,
    minQuantity: 1,
    regional: null,
    isImagenNueva: true,
    isImagenAnterior: true,
    formatCustom: false,
    image: null,
    tags: [],
    tagsInput: '',
    formats: [],
  };

  const formikArteFinal = useFormik({
    initialValues: initialValuesArteFinal,
    onSubmit: async (values) => {
      if (activeStep === 0) {
        if (values.category.length === 0) {
          toast.error('Debe seleccionar al menos una categoría');
          return;
        }

        const tagsProcesados = values.tagsInput
          .split(',')
          .map((t) => t.trim())
          .filter((t) => t.length > 0);

        const valuesConTags = {
          ...values,
          tags: tagsProcesados,
        };

        Object.entries(valuesConTags).forEach(([key, val]) => {
          formikArteFinal.setFieldValue(key, val, false);
        });

        if (!values.isDescargable) {
          setActiveStep(1);
        } else {
          const token = await getAccessTokenSilently();
          mutationFirstStep.mutate({ token, body: valuesConTags });
        }

        return;
      }

      if (activeStep === 1) {
        const token = await getAccessTokenSilently();

        const tagsProcesados = values.tagsInput
          .split(',')
          .map((t) => t.trim())
          .filter((t) => t.length > 0);

        const formats = values?.formats || [];

        for (let formatIndex = 0; formatIndex < formats.length; formatIndex++) {
          const format = formats[formatIndex];
          const providers = format.providers || [];

          for (let providerIndex = 0; providerIndex < providers.length; providerIndex++) {
            const providerItem = providers[providerIndex];

            const hasSomeValue = providerItem.code?.trim() || providerItem.provider || providerItem.price !== '';

            if (hasSomeValue) {
              const isComplete = providerItem.provider && providerItem.price !== '';

              if (!isComplete) {
                toast.error(
                  `El proveedor ${providerIndex + 1} del formato ${
                    formatIndex + 1
                  } está incompleto. Debes rellenar precio y proveedor.`,
                );
                return;
              }
            }
          }
        }

        // Comprobar códigos duplicados dentro del formulario
        const usedCodes = new Map();

        for (let formatIndex = 0; formatIndex < formats.length; formatIndex++) {
          const format = formats[formatIndex];
          const providers = format.providers || [];

          for (let providerIndex = 0; providerIndex < providers.length; providerIndex++) {
            const providerItem = providers[providerIndex];
            const code = providerItem.code?.trim();
            const normalizedCode = code?.toUpperCase();
            const allowDuplicateCode = !!providerItem.allowDuplicateCode;
            const isRepeatableCode = REPEATABLE_CODES.includes(normalizedCode);

            if (!code) continue;

            // Si este proveedor permite repetir código, no se valida
            if (allowDuplicateCode || isRepeatableCode) continue;

            if (usedCodes.has(code)) {
              const previous = usedCodes.get(code);

              toast.error(
                `El código "${code}" está repetido. Ya lo has usado en el proveedor ${
                  previous.providerIndex + 1
                } del formato ${previous.formatIndex + 1}.`,
              );
              return;
            }

            usedCodes.set(code, {
              formatIndex,
              providerIndex,
            });
          }
        }

        const formatsLimpios = (formats || []).map((format) => ({
          code: format.code?.trim() || '',
          name: format.name?.trim() || '',
          material: format.material || '',
          providers: (format.providers || [])
            .filter((providerItem) => providerItem.code?.trim() || providerItem.provider || providerItem.price !== '')
            .map((providerItem) => ({
              code: providerItem.code?.trim() || '',
              price: providerItem.price === '' ? null : Number(providerItem.price),
              provider: providerItem.provider || '',
              allowDuplicateCode: !!providerItem.allowDuplicateCode,
            })),
        }));

        const bodyFinal = {
          ...values,
          tags: tagsProcesados,
          formats: formatsLimpios,
        };

        mutationLastStep.mutate({
          token,
          body: bodyFinal,
        });
      }
    },
  });

  return {
    formikArteFinal,
    handleClose,
  };
};
